import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Stack, Barbell, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FromTemplateDialog({
  open,
  onOpenChange,
  programs,
  targetProgramId,
  setTargetProgramId,
  onConfirm,
}) {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSelectedId("");
    setSearch("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workout-templates`);
        if (!cancelled) setTemplates(data || []);
      } catch {
        toast.error("Errore nel caricamento dei template");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = templates.filter((t) =>
    `${t.title} ${t.description || ""}`.toLowerCase().includes(search.toLowerCase())
  );
  const selectedTpl = templates.find((t) => t.id === selectedId);

  const handleConfirm = () => {
    if (!selectedTpl) {
      toast.error("Seleziona un template");
      return;
    }
    onConfirm(selectedTpl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#171717] border-[#262626] text-white max-w-lg"
        data-testid="from-template-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="font-bold tracking-tight uppercase"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            APPLICA TEMPLATE
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">
              Programma di destinazione
            </Label>
            <Select
              value={targetProgramId || "none"}
              onValueChange={(v) => setTargetProgramId(v === "none" ? "" : v)}
            >
              <SelectTrigger
                className="mt-1 bg-[#0A0A0A] border-[#262626] text-white"
                data-testid="from-template-target-program"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626]">
                <SelectItem value="none" className="text-white">
                  Senza programma
                </SelectItem>
                {programs.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-white">
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Template</Label>
            <div className="relative mt-1 mb-2">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca template..."
                className="pl-9 bg-[#0A0A0A] border-[#262626] text-white text-sm"
                data-testid="template-search"
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
              {loading ? (
                <div className="text-center py-6 text-zinc-500 text-sm">Caricamento...</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-6 text-zinc-500 text-sm">
                  {templates.length === 0
                    ? "Nessun template salvato. Crealo dalla pagina Template o salva una scheda esistente."
                    : "Nessun template trovato"}
                </div>
              ) : (
                filtered.map((t) => {
                  const selected = selectedId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${
                        selected
                          ? "bg-[#007AFF]/10 border-[#007AFF]/50"
                          : "bg-[#0A0A0A] border-[#1A1A1A] hover:border-[#333]"
                      }`}
                      data-testid={`template-pick-${t.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Stack size={18} weight="duotone" className={selected ? "text-[#007AFF]" : "text-[#D4FF00]"} />
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${selected ? "text-white" : "text-zinc-200"}`}>
                            {t.title}
                          </p>
                          <p className="text-[11px] text-zinc-500 flex items-center gap-1.5 truncate">
                            <Barbell size={10} weight="fill" />
                            {t.exercises?.length || 0} esercizi
                            {t.description && <span className="truncate">— {t.description}</span>}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]"
            data-testid="from-template-cancel"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedTpl}
            className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
            data-testid="from-template-confirm"
          >
            Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
